import { formatInTimeZone } from 'date-fns-tz';
import truncate from 'lodash/truncate';

import { IEventsEvent, Maybe } from '@r-client/data/graphql-types';

import {
  ADD_TO_CALENDAR_URL,
  CALENDARS,
  E_CALENDAR,
  ORGANIZER_EMAIL,
  ORGANIZER_NAME,
} from '../constants';
import { getEventFullAddress, TEventAddress } from './get-event-full-address';

interface ICalendar {
  type: E_CALENDAR;
  displayName: string;
  url: string;
}

export type TCalendarEvent = Pick<
  IEventsEvent,
  'title' | 'url' | 'bodySanitized' | 'timezone' | 'beginAt' | 'endAt'
> & {
  address?: Maybe<TEventAddress>;
};

export enum E_PARAM_KEY {
  DATE_START = '&e[0][date_start]=',
  DATE_END = '&e[0][date_end]=',
  TIMEZONE = '&e[0][timezone]=',
  TITLE = '&e[0][title]=',
  DESCRIPTION = '&e[0][description]=',
  LOCATION = '&e[0][location]=',
  ORGANIZER = '&e[0][organizer]=',
  // eslint-disable-next-line @typescript-eslint/no-shadow
  ORGANIZER_EMAIL = '&e[0][organizer_email]=',
}

const MAX_BODY_LENGTH = 180;
const DATE_TIME_FORMAT = 'y-MM-dd kk:mm:ss';

export const getCalendarUrlParams = (calendarEvent: TCalendarEvent): string => {
  const { address, beginAt, bodySanitized, endAt, timezone, title, url } =
    calendarEvent;
  const location = address && getEventFullAddress(address);
  const bodyExcerpt =
    bodySanitized && truncate(bodySanitized, { length: MAX_BODY_LENGTH });

  const description =
    url && bodyExcerpt
      ? `${bodyExcerpt}\n\nEvent Link: ${url}`
      : bodyExcerpt ?? '';

  const params: string[] = [];

  params.push(
    `${E_PARAM_KEY.DATE_START}${formatInTimeZone(
      beginAt,
      timezone,
      DATE_TIME_FORMAT
    )}`
  );

  if (endAt) {
    params.push(
      `${E_PARAM_KEY.DATE_END}${formatInTimeZone(
        endAt,
        timezone,
        DATE_TIME_FORMAT
      )}`
    );
  }

  params.push(`${E_PARAM_KEY.TIMEZONE}${timezone}`);
  params.push(`${E_PARAM_KEY.TITLE}${title}`);
  params.push(`${E_PARAM_KEY.DESCRIPTION}${description}`);
  params.push(`${E_PARAM_KEY.LOCATION}${location || 'See description'}`);
  params.push(`${E_PARAM_KEY.ORGANIZER}${ORGANIZER_NAME}`);
  params.push(`${E_PARAM_KEY.ORGANIZER_EMAIL}${ORGANIZER_EMAIL}`);
  return params.join('');
};

export const getCalendarsCollection = (
  calendarEvent: TCalendarEvent
): ICalendar[] => {
  const calendarsCollection = CALENDARS.map((calendar) => {
    const url = `${ADD_TO_CALENDAR_URL}${
      calendar.type
    }?f=m${getCalendarUrlParams(calendarEvent)}`;
    return { ...calendar, url };
  });
  return calendarsCollection;
};
